import React from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { IconName } from '@fortawesome/fontawesome-svg-core'
import { Icon } from '@evertel/web/ui'

import { DragData } from './types'

interface DnDToolboxItemProps {
    id: string
    type: 'layout' | 'element'
    layoutType?: 'row' | 'spacer' | 'list'
    elementType?: string
    label: string
    icon: IconName
}

/**
 * Individual draggable item in the toolbox.
 * Uses useSortable for keyboard navigation support.
 * Items can be dragged but cannot receive drops.
 */
export const DnDToolboxItem: React.FC<DnDToolboxItemProps> = ({
    id,
    type,
    layoutType,
    elementType,
    label,
    icon
}) => {
    const dragData: DragData = {
        type,
        layoutType,
        elementType,
        fromToolbox: true
    }

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({
        id,
        data: dragData,
        disabled: {
            draggable: false, // Can still drag
            droppable: true   // Can't receive drops
        }
    })

    // Only apply transform when actively dragging - we don't want toolbox items
    // to shift around when other items are being sorted
    const style = {
        transform: isDragging ? CSS.Transform.toString(transform) : undefined,
        transition: isDragging ? transition : undefined,
        opacity: isDragging ? 0.5 : 1
    }

    return (
        <div
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            className={`sb-toolbox-item sb-toolbox-item--${type} ${isDragging ? 'shadow' : ''}`}
            style={style}
        >
            <Icon
                name={icon}
                fixedWidth
                className="me-2"
            />
            <span className="flex-grow-1">{label}</span>
            <Icon
                name="grip-dots-vertical"
                className="text-muted ps-2"
            />
        </div>
    )
}
